import React from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { APPS, ROLES } from 'components/constants';
import { has } from 'lodash';

/**
 * A React component that handles authorization logic based on user roles, permissions, and application context.
 * 
 * - app Authorized
 * - Permissions based on roleId, roleIds, perm, permKeys
 * - OR Permissions
 * - AND Permissions
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} [props.roleId] - A single role ID to check against the user's roles.
 * @param {string[]} [props.roleIds] - An array of role IDs to check against the user's roles.
 * @param {Object} [props.perm] - A permission object with a `key` property to check against user permissions.
 * @param {string[]} [props.permKeys] - An array of permission keys to check against user permissions.
 * @param {boolean} [props.or] - A fallback flag to grant access if no other conditions are met.
 * @param {string} [props.assetCustomer] - A specific asset customer role to check against the user's roles.
 * @param {string} [props.appId] - The application ID to match against the current application context.
 * @param {boolean} [props.show403] - Whether to display a 403 Unauthorized message if access is denied.
 * @param {React.ReactNode} props.children - The child components to render if the user is authorized.
 *
 * @returns {JSX.Element} The rendered component, either the children if authorized or a 403 message if not.
 */
export default function Authorize(props) {

    const params = useParams();
    
    const user = useSelector(store => store.users?.user);
    const features = useSelector((store) => store.settings?.settings?.features)?.[(params.appId == APPS.DISPATCH.id) ? 'dispatch' : 'assets'];
    const user_permissions = useSelector(store => store.users?.user_permissions);

    const hasPermission = ((props.roleId) ? user.roles?.includes(props.roleId) 
        :   (props.roleIds) ? user.roles?.filter(roleId => props.roleIds.includes(roleId)).length
        :   (props.perm) ? user_permissions[props.perm.key]
        :   (props.permKeys) ? props.permKeys.filter(key => user_permissions[key]).length
        :   true);

    const appAuthorized = (props.appId) ? params.appId == props.appId : true;
    const orPermission = props.or || false;
    const andPermission = (props.and == undefined) ? true : props.and;
    const featurePermission = (props.feature) ? features?.[props.feature] : true;

    const finalPermission = appAuthorized && (hasPermission || orPermission) && andPermission && featurePermission;

    return (
        <> { finalPermission
                ?   <>
                        { props.children }
                    </>
                :   <>
                        { props.show403 &&
                            <div className="middle-box text-center animated animation-delay-4 fadeInDown mb-5">
                                <h1>403</h1>
                                <h3 className="font-bold">Unauthorized</h3>

                                <div className="error-desc">
                                    Sorry, but you are not authorized for this resource
                                </div>
                            </div>
                        }
                    </>
            }
        </>
    );
};
